import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { State } from '../store/reducers';
import { setThemeData } from '../store';
import { useApi } from '../hooks';

export const useThemeSelector = (themeShortCode: string) => {
  const data = useSelector((state: State) => state.theme && state.theme[themeShortCode]);
  return { data };
};

export const useAnomalies = (themeShortCode: string) => {
  const dispatch = useDispatch();
  const data = useSelector((state: State) => state.theme && state.theme[themeShortCode]);
  const [selectedAnomalyType, setSelectedAnomalyType] = useState('');
  const { getAnomalies } = useApi();

  const anomalyTypes = useMemo(() => {
    return ((data && data.anomalyTypes) || [])
      .map((anomalyType) => {
        return {
          label: anomalyType.description,
          value: anomalyType.shortcode,
          anomalies: anomalyType.anomalies.map((anomaly) => ({
            label: anomaly.description,
            value: anomaly.shortCode,
          })),
        };
      })
      .sort((first: any, second: any) => first?.label?.localeCompare(second.label));
  }, [data]);

  const anomalies = useMemo(() => {
    if (selectedAnomalyType) {
      const anomalyType = anomalyTypes.find((anomaly) => anomaly.value === selectedAnomalyType);
      const selectedAnomalies = anomalyType ? anomalyType.anomalies : [];
      return selectedAnomalies.sort((firstAnomaly: any, secondAnomaly: any) =>
        firstAnomaly?.label?.localeCompare(secondAnomaly.label)
      );
    }
    return [];
  }, [selectedAnomalyType, anomalyTypes]);

  const handleOnSelectAnomalyType = useCallback(
    (form) => {
      const anomalyTypeShortCode = form.values.anomalyTypeShortCode;
      if (anomalyTypeShortCode !== selectedAnomalyType) {
        setSelectedAnomalyType(anomalyTypeShortCode);
      }
    },
    [selectedAnomalyType]
  );

  const allAnomalies = useMemo(() => {
    const anomalies = anomalyTypes.reduce((acc: any, next) => [...acc, ...next.anomalies], []);
    const filtered = anomalies.sort((firstAnomaly: any, secondAnomaly: any) => {
      //''.localeCompare
      return firstAnomaly?.label?.localeCompare(secondAnomaly?.label);
    });

    return filtered;
  }, [anomalyTypes]);

  const getAnomaliesByTheme = useCallback(async () => {
    if (!themeShortCode || data?.anomalyTypes) return;
    try {
      const anomalyTypes = await getAnomalies(themeShortCode);
      dispatch(setThemeData(themeShortCode, { anomalyTypes }));
    } catch (error) {
      console.log('get anomalies error', error);
    }
  }, [themeShortCode, data, getAnomalies, dispatch]);

  useEffect(() => {
    getAnomaliesByTheme();
  }, [getAnomaliesByTheme]);

  return {
    data,
    selectedAnomalyType,
    anomalyTypes,
    anomalies,
    allAnomalies,
    handleOnSelectAnomalyType,
  };
};

export const useResidences = () => {
  const dispatch = useDispatch();
  const { getResidences } = useApi();
  const residences = useSelector((state: State) => state.theme.residences);
  const residenceOptions = useMemo(() => {
    if (residences) {
      return Object.values(residences).map((residence: any) => ({
        label: residence.description,
        value: residence.shortCode,
      }));
    }
    return [];
  }, [residences]);

  const getResidencesList = useCallback(async () => {
    try {
      const residences = await getResidences();
      dispatch(setThemeData('residences', residences));
    } catch (error) {
      console.log('get residences error', error);
    }
  }, [dispatch, getResidences]);

  useEffect(() => {
    getResidencesList();
  }, [getResidencesList]);

  return { residences, residenceOptions };
};
